import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { createAppTheme, type ColorMode } from '../../theme';
import { useCompanyBrand } from '../../utils/company-brand';
import { NotifyProvider, UiTextProvider, type UiLocale } from '@routes-plan/ui';
import { currentLang } from '../../i18n';

/** Preferencia de la persona: seguir al sistema, o fijar claro u oscuro. */
export type ColorModePreference = 'system' | ColorMode;

const STORAGE_KEY = 'ruttia.colorMode';
const ORDER: ColorModePreference[] = ['system', 'light', 'dark'];

const readPreference = (): ColorModePreference => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'system' || saved === 'light' || saved === 'dark') return saved;
  } catch {
    /* sin almacenamiento: se sigue al sistema */
  }
  return 'system';
};

interface ColorModeValue {
  preference: ColorModePreference;
  mode: ColorMode;
  setPreference: (value: ColorModePreference) => void;
  /** Sistema → claro → oscuro → sistema. */
  cyclePreference: () => void;
}

const ColorModeContext = createContext<ColorModeValue>({
  preference: 'system',
  mode: 'light',
  setPreference: () => undefined,
  cyclePreference: () => undefined,
});

export const useColorMode = () => useContext(ColorModeContext);

/**
 * Tema de Material UI en modo claro u oscuro según la preferencia (por defecto, la del sistema).
 * También marca `<html data-theme>` y `color-scheme` para el CSS que no es de Material UI
 * (calendarios, mapas) y para los controles nativos del navegador.
 */
export const ColorModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const systemDark = useMediaQuery('(prefers-color-scheme: dark)', { noSsr: true });
  const [preference, setPreferenceState] = useState<ColorModePreference>(readPreference);
  const mode: ColorMode = preference === 'system' ? (systemDark ? 'dark' : 'light') : preference;

  const setPreference = useCallback((value: ColorModePreference) => {
    setPreferenceState(value);
    try {
      localStorage.setItem(STORAGE_KEY, value);
    } catch {
      /* sin almacenamiento: dura hasta recargar */
    }
  }, []);

  const cyclePreference = useCallback(() => {
    setPreference(ORDER[(ORDER.indexOf(preference) + 1) % ORDER.length]);
  }, [preference, setPreference]);

  useEffect(() => {
    const root = document.documentElement;
    root.dataset.theme = mode;
    root.style.colorScheme = mode;
  }, [mode]);

  const company = useCompanyBrand();
  const theme = useMemo(() => createAppTheme(mode, company), [mode, company]);
  const value = useMemo(() => ({ preference, mode, setPreference, cyclePreference }), [preference, mode, setPreference, cyclePreference]);

  return (
    <ColorModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline enableColorScheme />
        {/* Textos de la librería de UI en el idioma de la persona y avisos flotantes estándar. */}
        <UiTextProvider locale={currentLang() as UiLocale}>
          <NotifyProvider>{children}</NotifyProvider>
        </UiTextProvider>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
};
